import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import {
    Button,
    LoadingSkeleton,
    Markdown,
    Modal,
    ModalBody,
    Text,
} from "@wojo/ui";
import React, { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { getCurrency } from "../../../../hooks";
import { usePaymentScheduleSuspenseMutation } from "../hooks";

export const QuoteAutopay: React.FC = () => {
    const { productPage } = useI18n("productPage");
    return (
        <ErrorBoundary fallback={null}>
            <Suspense
                fallback={
                    <LoadingSkeleton
                        accessibleText={productPage.tripBuilder.paymentLoading.l()}
                        style={{
                            marginTop: "var(--g-spacing-lg)",
                            marginLeft: "var(--g-spacing-md)",
                            marginRight: "var(--g-spacing-md)",
                        }}
                        width="calc(100% - 2 * var(--g-spacing-md))"
                        height="44px"
                    />
                }
            >
                <QuoteAutopayComponent />
            </Suspense>
        </ErrorBoundary>
    );
};

const QuoteAutopayComponent: React.FC = () => {
    const { formatCurrency } = getCurrency();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const paymentSchedule = usePaymentScheduleSuspenseMutation();
    const { productPage } = useI18n("productPage");
    if (!paymentSchedule) {
        return null;
    }
    const modalTriggerLabel =
        productPage.tripBuilder.autoPay.modalTriggerLabel.l();
    const deposit =
        typeof paymentSchedule.autoPayCheckoutOption?.amount.value === "number"
            ? formatCurrency(paymentSchedule.autoPayCheckoutOption.amount.value)
            : "";

    return (
        <>
            <Text.Body
                style={{
                    textAlign: "center",
                    paddingLeft: "var(--g-spacing-md)",
                    paddingRight: "var(--g-spacing-md)",
                    marginTop: "var(--g-spacing-lg)",
                }}
                tag="div"
            >
                {!!deposit && (
                    <Markdown
                        markdown={productPage.tripBuilder.autoPay.description.l(
                            {
                                numberOfPayments: `${paymentSchedule.numberOfPayments}`,
                                paymentAmount: formatCurrency(
                                    Math.ceil(paymentSchedule.recurringPayment),
                                ),
                                deposit,
                            },
                        )}
                    />
                )}
                {modalTriggerLabel && (
                    <Button
                        variant="quiet"
                        type="button"
                        onClick={() => {
                            gtmSendEvent({
                                event: "track",
                                ga4Tracking: {
                                    ga4_object: "TRIP_BUILDER",
                                    ga4_action: "MODAL_OPENED",
                                    modalId: "AutoPay Info",
                                },
                            });
                            setIsModalOpen(true);
                        }}
                    >
                        {modalTriggerLabel}
                    </Button>
                )}
            </Text.Body>
            <Modal
                isOpen={isModalOpen}
                accessibleTitle={productPage.tripBuilder.autoPay.modal.heading.l()}
                onCloseRequested={() => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "MODAL_CLOSED",
                            modalId: "AutoPay Info",
                        },
                    });
                    setIsModalOpen(false);
                }}
            >
                <ModalBody>
                    <Text.Heading
                        order="3"
                        tag="h2"
                        style={{ marginBottom: "var(--g-spacing-md)" }}
                    >
                        {productPage.tripBuilder.autoPay.modal.heading.l()}
                    </Text.Heading>
                    <Text.Body>
                        <Markdown
                            markdown={productPage.tripBuilder.autoPay.modal.description.l(
                                { deposit },
                            )}
                        />
                    </Text.Body>
                </ModalBody>
            </Modal>
        </>
    );
};
